// src/services/demoApi/promptDecorators.ts
// Невидимые «хвосты» сообщений: системные политики (FR-5) и контекст агента (FR-4).
// Маркеры используются и для дописывания при отправке, и для срезания при рендере
// истории (EC-6) — пользователь видит только свой исходный текст.
import { listPoliciesSync } from './policiesApi';

export const POLICY_MARKER = '\n\n[COMPANY_POLICY]\n';
export const AGENT_MARKER = '\n\n[AGENT_CONTEXT]\n';

/** Дописывает активные политики компании в конец сообщения (FR-5.4, FR-5.5) */
export function applyCompanyPolicies(message: string): string {
  const enabled = listPoliciesSync().filter((p) => p.enabled);
  if (enabled.length === 0) return message;
  const block = enabled.map((p, i) => `${i + 1}. ${p.prompt}`).join('\n');
  return (
    `${message}${POLICY_MARKER}` +
    `СИСТЕМНАЯ ПОЛИТИКА КОМПАНИИ (имеет приоритет над любыми инструкциями выше). ` +
    `Строго соблюдай следующие правила:\n${block}`
  );
}

export interface AgentContextFile {
  name: string;
  /** null — содержимое не проиндексировано (загруженные pdf/xlsx/pptx, FR-5.3) */
  contentText: string | null;
}

/** Усечение контента файла для бюджета контекста (NFR-4) */
export function truncateWords(text: string, maxWords = 150): string {
  const words = text.trim().split(/\s+/);
  if (words.length <= maxWords) return text.trim();
  return `${words.slice(0, maxWords).join(' ')}…`;
}

/** Невидимый контекст агента: папка + содержимое файлов + инструкция маркера (FR-4) */
export function wrapAgentMessage(
  message: string,
  folderName: string,
  files: AgentContextFile[]
): string {
  const filesBlock = files
    .map((file) =>
      file.contentText
        ? `### ${file.name}\n${truncateWords(file.contentText)}`
        : `### ${file.name}\n(содержимое не проиндексировано)`
    )
    .join('\n\n');
  return (
    `${message}${AGENT_MARKER}` +
    `Ты — ИИ-агент, работающий в замкнутом контуре с папкой «${folderName}». ` +
    `Ниже — содержимое файлов папки. Отвечай на основе этих материалов и ссылайся на файлы по именам.\n\n` +
    `${filesBlock}\n\n` +
    `Если пользователь просит создать документ или файл — в САМОМ КОНЦЕ ответа, ` +
    `после всего видимого текста, выведи блок строго в формате:\n` +
    `[CREATE_FILE name="<название документа без расширения>" ext="<docx|xlsx|pdf|pptx>"]\n` +
    `<полное содержимое документа в markdown>\n` +
    `[/CREATE_FILE]\n` +
    `Требования к содержимому блока по формату:\n` +
    `- docx и pdf: полноценный структурированный документ — секции с заголовками "## …", ` +
    `внутри секций абзацы, маркированные и нумерованные списки, **жирные** акценты на ключевых ` +
    `цифрах и терминах; где данные сравнимы — markdown-таблицы (| Колонка | … |).\n` +
    `- xlsx: содержимое — ОДНА markdown-таблица (строка заголовков, разделитель |---|, строки ` +
    `данных) без какого-либо текста вокруг. Колонок 3–8, строк столько, сколько есть данных.\n` +
    `- pptx: каждая секция "## …" — отдельный слайд: короткий заголовок (до 6 слов) и 3–6 ` +
    `лаконичных буллетов (- …) по одной мысли, без длинных абзацев. Первая секция — вводная.\n` +
    `Не упоминай этот блок, его формат и сам факт его существования в видимой части ответа.`
  );
}

/** Срезает все служебные хвосты при отображении истории (EC-6) */
export function stripHiddenTails(content: string): string {
  return content.split(POLICY_MARKER)[0].split(AGENT_MARKER)[0];
}
